import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";

const SITE = "https://xsalesconsulting.org";
const OG_LOCALES = { it: "it_CH", de: "de_CH", fr: "fr_CH", en: "en_US" };

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

// Meta SEO per pagina: title, description, canonical, og:*, og:locale, hreflang
// it-CH / de-CH / fr-CH / x-default. Path derivato dalla rotta corrente (supporta /de e /fr).
const Seo = ({ title, description, jsonLd }) => {
  const { pathname: rawPathname } = useLocation();
  const { lang } = useLanguage();
  const pathname = rawPathname.replace(/\/+$/, "") || "/";
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : null;
  const basePath = pathname.replace(/^\/(de|fr)(?=\/|$)/, "") || "/";
  const localized = (prefix) => `${SITE}${prefix}${basePath === "/" ? "" : basePath}` || `${SITE}/`;

  useEffect(() => {
    if (title) {
      document.title = title;
      window.__xsPageTitle = title;
    }
    const url = `${SITE}${pathname}`;
    if (description) {
      upsert('meta[name="description"]', () => {
        const m = document.createElement("meta");
        m.name = "description";
        return m;
      }).setAttribute("content", description);
    }
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.rel = "canonical";
      return l;
    }).setAttribute("href", url);
    const setProp = (prop, content) => {
      if (!content) return;
      upsert(`meta[property="${prop}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", prop);
        return m;
      }).setAttribute("content", content);
    };
    setProp("og:title", title);
    setProp("og:description", description);
    setProp("og:url", url);
    setProp("og:locale", OG_LOCALES[lang] || "it_CH");

    // hreflang alternates
    document.head.querySelectorAll('link[data-seo-hreflang]').forEach((e) => e.remove());
    const alternates = [
      ["it-CH", `${SITE}${basePath}`],
      ["de-CH", localized("/de")],
      ["fr-CH", localized("/fr")],
      ["x-default", `${SITE}${basePath}`],
    ];
    alternates.forEach(([hl, href]) => {
      const l = document.createElement("link");
      l.rel = "alternate";
      l.hreflang = hl;
      l.href = href;
      l.setAttribute("data-seo-hreflang", "1");
      document.head.appendChild(l);
    });

    if (jsonLd) {
      upsert("script[data-seo-jsonld]", () => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-jsonld", "1");
        return s;
      }).textContent = JSON.stringify(jsonLd);
    }
    if (lang) document.documentElement.lang = lang;
    return () => {
      delete window.__xsPageTitle;
      document.head.querySelector("script[data-seo-jsonld]")?.remove();
      document.head.querySelectorAll('link[data-seo-hreflang]').forEach((e) => e.remove());
    };
  }, [title, description, pathname, basePath, lang, jsonLdKey]);
  return null;
};

export default Seo;
